'use client';

import React, { useRef } from 'react';
import { ImagePlus, X } from 'lucide-react';

export interface UploadedPhoto {
  id: string;
  name: string;
  dataUrl: string;
}

interface PhotoUploadProps {
  photos: UploadedPhoto[];
  onChange: (photos: UploadedPhoto[]) => void;
  max?: number;
  hint?: string;
}

const readAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Photo capture (e.g. insurance card / ID). Kept as base64 in form state.
export function PhotoUpload({ photos, onChange, max = 4, hint }: PhotoUploadProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const room = Math.max(0, max - photos.length);
    const incoming = Array.from(files).slice(0, room);
    const added = await Promise.all(
      incoming.map(async (file) => ({
        id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: file.name,
        dataUrl: await readAsDataUrl(file),
      })),
    );
    onChange([...photos, ...added]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const remove = (id: string) => onChange(photos.filter((p) => p.id !== id));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {photos.map((photo) => (
          <div key={photo.id} className="group relative h-24 w-24 overflow-hidden rounded-xl border border-gray-200">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={photo.dataUrl} alt={photo.name} className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => remove(photo.id)}
              className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white/90 text-gray-500 shadow-sm transition-colors hover:text-red-500"
              aria-label={`Remove ${photo.name}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}

        {photos.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-gray-300 text-gray-400 transition-colors hover:border-hprimary-400 hover:bg-hprimary-50 hover:text-hprimary-600"
          >
            <ImagePlus className="h-5 w-5" />
            <span className="text-xs">Add photo</span>
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}
